import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Papa from "papaparse";
import { toast } from "sonner";
import { AgGridReact } from "ag-grid-react";
import { ColDef, CellClassParams, IRowNode } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { Input } from "../../../components/ui/input";
import { useTheme } from "../../../contexts/themeContext";

interface CSVViewerProps {
  url: string | null;
  visible?: boolean;
  fileName?: string;
  defaultSearchValue?: string;
  elementId?: string;
}

const CSVViewer: React.FC<CSVViewerProps> = ({
  url,
  visible = true,
  fileName,
  elementId,
  defaultSearchValue = "",
}) => {
  const { resolvedTheme } = useTheme();
  const [data, setData] = useState<string[][]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRange, setSelectedRange] = useState<{
    startRow: number;
    endRow: number;
    startCol: number;
    endCol: number;
  } | null>(null);

  const [searchValue, setSearchValue] = useState(defaultSearchValue);
  const gridRef = useRef<AgGridReact>(null);

  const parseSingleCell = useCallback(
    (cellId: string): { row: number; col: number } | null => {
      const match = cellId.match(/row_(\d+)_column_([A-Za-z]+|\d+)/);
      if (!match) return null;

      const rowIndex = parseInt(match[1]);
      let colIndex: number;

      const columnPart = match[2];
      if (/^\d+$/.test(columnPart)) {
        // Numeric column (e.g., "0", "1", "2")
        colIndex = parseInt(columnPart);
      } else {
        // Letter column (e.g., "A", "B", "C")
        colIndex = columnPart.toUpperCase().charCodeAt(0) - 65;
      }

      return { row: rowIndex, col: colIndex };
    },
    [],
  );
  const parseElementId = useCallback(
    (
      elementId: string,
    ): {
      startRow: number;
      endRow: number;
      startCol: number;
      endCol: number;
    } | null => {
      if (!elementId) return null;

      // Check if it's a range format like "row_2_column_A:row_11_column_A"
      const rangeMatch = elementId.match(
        /^(row_\d+_column_[A-Za-z0-9]+):(row_\d+_column_[A-Za-z0-9]+)$/,
      );
      if (rangeMatch) {
        const startCell = parseSingleCell(rangeMatch[1]);
        const endCell = parseSingleCell(rangeMatch[2]);

        if (!startCell || !endCell) return null;

        return {
          startRow: Math.min(startCell.row, endCell.row),
          endRow: Math.max(startCell.row, endCell.row),
          startCol: Math.min(startCell.col, endCell.col),
          endCol: Math.max(startCell.col, endCell.col),
        };
      }

      // Handle single cell format like "row_1_column_A" or "row_1_column_0"
      const singleCell = parseSingleCell(elementId);
      if (!singleCell) return null;

      return {
        startRow: singleCell.row,
        endRow: singleCell.row,
        startCol: singleCell.col,
        endCol: singleCell.col,
      };
    },
    [parseSingleCell],
  );

  const scrollToElement = (range: {
    startRow: number;
    endRow: number;
    startCol: number;
    endCol: number;
  }) => {
    if (!gridRef.current?.api) return;

    const gridApi = gridRef.current.api;

    // Adjust for header row - CSV rows start from 1 (header), AG-Grid data rows start from 0
    const adjustedRange = {
      startRow: Math.max(0, range.startRow - 2),
      endRow: Math.max(0, range.endRow - 2),
      startCol: range.startCol,
      endCol: range.endCol,
    };

    setSelectedRange(adjustedRange);

    setTimeout(() => {
      // Scroll to the start of the range
      gridApi.ensureIndexVisible(adjustedRange.startRow);

      const columnId = `col_${adjustedRange.startCol}`;
      gridApi.ensureColumnVisible(columnId);
    }, 100);
  };

  useEffect(() => {
    if (!url || !visible) return;
    setLoading(true);
    setError(null);
    setData([]);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch CSV");
        return res.text();
      })
      .then((csvText) => {
        const parsed = Papa.parse<string[]>(csvText, { dynamicTyping: false });
        if (parsed.errors.length > 0) {
          toast.error("Failed to parse CSV");
          setError("Failed to parse CSV");
        } else {
          setData(parsed.data as string[][]);
        }
      })
      .catch((e) => {
        toast.error("Failed to load CSV");
        setError("Failed to load CSV");
        console.error(e);
      })
      .finally(() => setLoading(false));
  }, [url, visible]);

  useEffect(() => {
    if (!elementId || !data.length || loading) return;

    const parsedElement = parseElementId(elementId);
    if (!parsedElement) return;

    if (parsedElement.startCol < 0 || parsedElement.endCol >= data[0].length)
      return;

    setTimeout(() => {
      scrollToElement(parsedElement);
    }, 500);
  }, [elementId, data, loading, parseElementId]);

  const columnDefs: ColDef[] = useMemo(() => {
    if (!data.length) return [];
    const headerRow = data[0];
    return headerRow.map((header, colIdx) => ({
      headerName: header || `Column ${colIdx + 1}`,
      field: `col_${colIdx}`,
      resizable: true,
      sortable: true,
      filter: true,
      cellStyle: (params: CellClassParams): Record<string, string> => {
        if (
          selectedRange &&
          params.node.rowIndex !== null &&
          params.node.rowIndex >= selectedRange.startRow &&
          params.node.rowIndex <= selectedRange.endRow &&
          colIdx >= selectedRange.startCol &&
          colIdx <= selectedRange.endCol
        ) {
          return { background: "rgba(0, 0, 255, 0.1)" };
        }
        return {};
      },
    }));
  }, [data, selectedRange]);

  const rowData = useMemo(() => {
    if (!data.length) return [];
    return data.slice(1).map((rowArr) => {
      const rowObj: Record<string, string> = {};
      rowArr.forEach((cell, colIdx) => {
        rowObj[`col_${colIdx}`] = cell;
      });
      return rowObj;
    });
  }, [data]);

  // Try to extract a date from a string in various formats
  const extractDateParts = (
    str: string,
  ): { year: string; month: string; day: string } | null => {
    // Check for ISO format: YYYY-MM-DD
    let match = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (match) {
      return {
        year: match[1],
        month: match[2].padStart(2, "0"),
        day: match[3].padStart(2, "0"),
      };
    }

    // Check for MM/DD/YYYY format
    match = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) {
      return {
        year: match[3],
        month: match[1].padStart(2, "0"),
        day: match[2].padStart(2, "0"),
      };
    }

    // Check for DD/MM/YYYY format
    match = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) {
      // This is ambiguous with MM/DD/YYYY, but we'll include it as an option
      return {
        year: match[3],
        month: match[2].padStart(2, "0"),
        day: match[1].padStart(2, "0"),
      };
    }

    // Check for YYYY/MM/DD format
    match = str.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
    if (match) {
      return {
        year: match[1],
        month: match[2].padStart(2, "0"),
        day: match[3].padStart(2, "0"),
      };
    }

    // Check for MM-DD-YYYY format
    match = str.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (match) {
      return {
        year: match[3],
        month: match[1].padStart(2, "0"),
        day: match[2].padStart(2, "0"),
      };
    }

    // Check for DD-MM-YYYY format
    match = str.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (match) {
      // This is ambiguous with MM-DD-YYYY, but we'll include it as an option
      return {
        year: match[3],
        month: match[2].padStart(2, "0"),
        day: match[1].padStart(2, "0"),
      };
    }

    // Check for MMM DD, YYYY format (Jan 01, 2024)
    match = str.match(/^([A-Za-z]{3,}) (\d{1,2}),? (\d{4})$/);
    if (match) {
      const monthMap: Record<string, string> = {
        jan: "01",
        feb: "02",
        mar: "03",
        apr: "04",
        may: "05",
        jun: "06",
        jul: "07",
        aug: "08",
        sep: "09",
        oct: "10",
        nov: "11",
        dec: "12",
      };

      const monthAbbr = match[1].toLowerCase().substring(0, 3);
      const month = monthMap[monthAbbr];

      if (month) {
        return {
          year: match[3],
          month: month,
          day: match[2].padStart(2, "0"),
        };
      }
    }

    return null;
  };

  // Compare two dates for equality, handling various formats
  const datesMatch = (date1: string, date2: string): boolean => {
    const parts1 = extractDateParts(date1);
    const parts2 = extractDateParts(date2);

    if (!parts1 || !parts2) return false;

    return (
      parts1.year === parts2.year &&
      parts1.month === parts2.month &&
      parts1.day === parts2.day
    );
  };

  // Custom filter function for the AG-Grid
  const isExternalFilterPresent = () => {
    return searchValue.trim().length > 0;
  };

  const doesExternalFilterPass = (node: IRowNode) => {
    if (!searchValue.trim()) return true;

    const searchTerm = searchValue.toLowerCase();

    // Try different data properties
    for (const key in node.data) {
      const cellValue = (node.data[key]?.toString() || "").trim();

      // Direct match
      if (cellValue.toLowerCase().includes(searchTerm)) {
        return true;
      }

      // Try date matching if the search might be a date
      if (extractDateParts(searchValue) && extractDateParts(cellValue)) {
        if (datesMatch(searchValue, cellValue)) {
          return true;
        }
      }
    }

    return false;
  };

  if (!visible) return null;

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500 mx-auto mb-4"></div>
          <p>Loading CSV...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <p>Error loading CSV</p>
        </div>
      </div>
    );
  }

  if (!data.length) {
    return <div className="text-center text-gray-500">No CSV data loaded</div>;
  }

  return (
    <div className="w-full h-full flex flex-col overflow-auto">
      {fileName && <div className="p-2 font-semibold">{fileName}</div>}
      <div className="flex p-3 flex-col gap-2">
        <div>Search</div>
        <Input
          placeholder="Search"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </div>
      <div className="grow overflow-auto p-3">
        <div
          className={
            resolvedTheme === "dark"
              ? "ag-theme-balham-dark"
              : "ag-theme-balham"
          }
          style={{ width: "100%", height: "100%" }}
        >
          <AgGridReact
            ref={gridRef}
            domLayout="autoHeight"
            columnDefs={columnDefs}
            rowData={rowData}
            suppressCellFocus={true}
            isExternalFilterPresent={isExternalFilterPresent}
            doesExternalFilterPass={doesExternalFilterPass}
          />
        </div>
      </div>
      <style>{`
        /* Highlighting handled by cellStyle */
      `}</style>
    </div>
  );
};

export default CSVViewer;
